import { Business } from './business'
import { CFDI, PaymentMethod, TaxInformation } from './invoices'

export interface Currency {
  isoCode: string
  name: string
  symbol: string
}

export enum UserRole {
  ADMIN = 'ADMIN',
  REVIEWER = 'REVIEWER',
  EMPLOYEE = 'EMPLOYEE'
}

export const UserRoleOptions = [
  { label: 'roles.adminOption', value: UserRole.ADMIN },
  { label: 'expenses.reviewer', value: UserRole.REVIEWER },
  { label: 'common.user', value: UserRole.EMPLOYEE }
]

export const UserRoleTranslator: Record<UserRole, string> = {
  ADMIN: 'roles.admin',
  REVIEWER: 'expenses.reviewer',
  EMPLOYEE: 'common.user'
}

export interface UserForwardEmailAlias {
  id: number
  user_id: number
  email: string
  created_at: Date
  updated_at: Date
}

export interface User {
  id: number
  email: string
  name?: string
  phone_numbers: string[]
  created_at?: Date
  updated_at?: Date
  deleted_at?: Date
  role: UserRole
  businesses: Business[]
  supa_id?: string
  forward_email_alias?: UserForwardEmailAlias | null
  default_reviewer?: User | null
  reviewed_users?: User[]
}

export interface UserSettings {
  payment_method?: PaymentMethod | null
  cfdi?: CFDI | null
  user_id: number
  default_reviewer_id?: number | null
  receive_reviewer_emails?: boolean
  download_field_labels?: Record<string, string> | null
}

export interface UpdateUserForm extends Omit<User, 'created_at' | 'updated_at' | 'phone_numbers' | 'businesses'> {
  phone_number: string
  password?: string | null
  businesses: number[]
  cfdi?: CFDI | null
  payment_method?: PaymentMethod | null
  default_reviewer_id?: number | null
  reviewed_user_ids?: number[]
}

export type CreateUserForm = {
  email: string
  phone_number: string
  name?: string
  password: string
  tax_information: Array<number>
  role: UserRole
  cfdi?: CFDI | null
  payment_method?: PaymentMethod | null
  businesses: number[]
  default_reviewer_id?: number | null
  reviewed_user_ids?: number[]
}

export type UserFormValues = UpdateUserForm | CreateUserForm

/**
 * Signed-in user held in the store. Carries the access token only: the API
 * stopped returning refresh tokens, and the app re-authenticates on a 401
 * instead of renewing.
 */
export interface AuthUser extends User {
  token: string
}

type Primitive = string | number | boolean | null
type FlattenPairs<T> = { [K in keyof T]: T[K] extends Primitive ? [K, T[K]] : FlattenPairs<T[K]> }[keyof T] & [PropertyKey, Primitive]
export type Flatten<T> = { [P in FlattenPairs<T> as P[0]]: P[1] }

export enum ProductType {
  product = 'product',
  invoices = 'invoices',
  seat = 'seat',
  tax_information = 'tax_information',
  expenses = 'expenses',
  expense_reviews = 'expense_reviews',
  budgets = 'budgets',
  bank_statement_reconciliation = 'bank_statement_reconciliation'
}

export const TranslatedProductType: Record<string, string> = {
  invoices: 'productTypes.invoices',
  seat: 'productTypes.seat',
  tax_information: 'productTypes.taxInformation',
  expenses: 'productTypes.expenses',
  expense_reviews: 'productTypes.expenseReviews',
  budgets: 'nav.budgets',
  bank_statement_reconciliation: 'productTypes.bankStatementReconciliation'
}

export const PRODUCTS_WITH_LIMIT: ProductType[] = [ProductType.invoices, ProductType.seat, ProductType.tax_information, ProductType.expenses]

export const shouldShowProductLimit = (productType: ProductType): boolean => {
  return PRODUCTS_WITH_LIMIT.includes(productType)
}

export interface Limit {
  max: number
}
export interface Product {
  id: number
  name: string
  stripe_price_id?: string
  type: ProductType
  limit?: Limit

  created_at?: Date
  updated_at?: Date
}

export enum GroupRole {
  ADMIN = 'ADMIN',
  EMPLOYEE = 'EMPLOYEE'
}

export interface Group {
  id: number
  name: string
  role: GroupRole
}

/** Channel a password-recovery code is delivered through. */
export type RecoveryChannel = 'email' | 'whatsapp'
